import React, { useState } from 'react';
import { Plus, Copy, Briefcase } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import ServiceItem from './ServiceItem';

const ServicesTab = ({
  item,
  onUpdate,
  selectedType,
  units,
  themeColors
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [expandedServices, setExpandedServices] = useState(new Set());

  // Add new service to the item
  const addService = () => {
    const newService = {
      id: Date.now(),
      service_number: '',
      service_description: '',
      service_quantity: 1,
      service_unit: 'STK',
      service_gross_price: '',
      service_currency: 'EUR',
      service_cost_center: '',
      service_auftrag: '',
      service_gl_account: ''
    };
    onUpdate(item.id, 'services', [...(item.services || []), newService]);
    // Collapse all other services and expand only the new one
    setExpandedServices(new Set([newService.id]));
  };

  // Copy existing service (duplicate the expanded service)
  const copyService = () => {
    if (!item.services || item.services.length === 0) return;
    
    // Find the currently expanded service
    const expandedServiceIds = Array.from(expandedServices);
    let serviceToCopy;
    
    if (expandedServiceIds.length > 0) {
      // Use the first expanded service
      serviceToCopy = item.services.find(service => expandedServiceIds.includes(service.id));
    }
    
    // If no service is expanded, use the last service as fallback
    if (!serviceToCopy) {
      serviceToCopy = item.services[item.services.length - 1];
    }
    
    const copiedService = {
      ...serviceToCopy,
      id: Date.now() + Math.random() * 1000, // New unique ID
      service_description: `${serviceToCopy.service_description} ${t('banf.services.copySuffix')}`
    };
    
    onUpdate(item.id, 'services', [...item.services, copiedService]);
    // Expand only the new copied service
    setExpandedServices(new Set([copiedService.id]));
  };

  // Remove service from the item
  const removeService = (serviceId) => {
    const updatedServices = (item.services || []).filter(service => service.id !== serviceId);
    onUpdate(item.id, 'services', updatedServices);
    // Remove from expanded services
    setExpandedServices(prev => {
      const newSet = new Set(prev);
      newSet.delete(serviceId);
      return newSet;
    });
  };

  // Update specific service field
  const updateService = (serviceId, field, value) => {
    const updatedServices = (item.services || []).map(service =>
      service.id === serviceId ? { ...service, [field]: value } : service
    );
    onUpdate(item.id, 'services', updatedServices);
  };

  // Toggle expand/collapse for a service
  const toggleServiceExpansion = (serviceId) => {
    setExpandedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      {/* Add Service Button */}
      <div className="flex justify-between items-center">
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {t('banf.services.count', { count: (item.services || []).length })}
        </h3>
        <div className="flex items-center space-x-3">
          {/* Kopieren Button - nur anzeigen wenn Leistungen vorhanden */}
          {(item.services && item.services.length > 0) && (
            <button
              onClick={copyService}
              className="relative group/copyServices overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(29,78,216,0.6) 100%)'
                  : 'linear-gradient(135deg, rgba(59,130,246,0.9) 0%, rgba(37,99,235,0.9) 50%, rgba(29,78,216,0.9) 100%)',
                backdropFilter: isDarkMode ? 'blur(20px)' : 'blur(16px)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.6)',
                boxShadow: isDarkMode 
                  ? '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(59,130,246,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)'
              }}
              title={t('banf.services.copyExpanded')}
            >
              {/* Smooth Hover Overlay */}
              <div 
                className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/copyServices:opacity-100"
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 100%)'
                }}
              ></div>
                                  
              <div className="relative z-10 px-3 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/copyServices:drop-shadow-xl">
                <Copy className="w-4 h-4 mr-1.5 transition-transform duration-300 group-hover/copyServices:scale-110" />
                {t('banf.services.copy')}
              </div>
              
              {/* Smooth Wave Effect */}
              <div className="absolute inset-0 opacity-0 group-hover/copyServices:opacity-100 transition-opacity duration-500">
                <div 
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/copyServices:translate-x-full transition-transform duration-800"
                  style={{
                    background: isDarkMode
                      ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                      : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)'
                  }}
                ></div>
              </div>
            </button>
          )}
          
          <button
            onClick={addService}
            className="relative group/addService overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: selectedType === 'kostenstelle' && (item.services && item.services.length > 0)
                ? (isDarkMode 
                  ? 'linear-gradient(135deg, rgba(249,115,22,0.7) 0%, rgba(251,146,60,0.7) 50%, rgba(234,88,12,0.7) 100%)'  // Orange gradient when services exist
                  : 'linear-gradient(135deg, rgba(249,115,22,0.9) 0%, rgba(251,146,60,0.9) 50%, rgba(234,88,12,0.9) 100%)')
                : selectedType === 'kostenstelle'
                ? (isDarkMode
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.6) 0%, rgba(16,185,129,0.6) 50%, rgba(5,150,105,0.6) 100%)'  // Green for Kostenstelle without services
                  : 'linear-gradient(135deg, rgba(34,197,94,0.9) 0%, rgba(16,185,129,0.9) 50%, rgba(5,150,105,0.9) 100%)')
                : selectedType === 'auftrag' && (item.services && item.services.length > 0)
                ? (isDarkMode
                  ? 'linear-gradient(135deg, rgba(249,115,22,0.7) 0%, rgba(251,146,60,0.7) 50%, rgba(234,88,12,0.7) 100%)'  // Orange gradient for Auftrag with services
                  : 'linear-gradient(135deg, rgba(249,115,22,0.9) 0%, rgba(251,146,60,0.9) 50%, rgba(234,88,12,0.9) 100%)')
                : selectedType === 'auftrag'
                ? (isDarkMode
                  ? 'linear-gradient(135deg, rgba(147,51,234,0.7) 0%, rgba(168,85,247,0.7) 50%, rgba(126,34,206,0.7) 100%)'  // Purple for Auftrag without services
                  : 'linear-gradient(135deg, rgba(147,51,234,0.9) 0%, rgba(168,85,247,0.9) 50%, rgba(126,34,206,0.9) 100%)')
                : (isDarkMode 
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.6) 0%, rgba(16,185,129,0.6) 50%, rgba(5,150,105,0.6) 100%)'  // Default green
                  : 'linear-gradient(135deg, rgba(34,197,94,0.9) 0%, rgba(16,185,129,0.9) 50%, rgba(5,150,105,0.9) 100%)'),
              backdropFilter: isDarkMode ? 'blur(20px)' : 'blur(16px)',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.6)',
              boxShadow: selectedType === 'kostenstelle' && (item.services && item.services.length > 0)
                ? (isDarkMode 
                  ? '0 8px 32px rgba(249,115,22,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(249,115,22,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)')
                : selectedType === 'auftrag' && (item.services && item.services.length > 0)
                ? (isDarkMode 
                  ? '0 8px 32px rgba(249,115,22,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(249,115,22,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)')
                : selectedType === 'kostenstelle'
                ? (isDarkMode
                  ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(34,197,94,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)')
                : selectedType === 'auftrag'
                ? (isDarkMode
                  ? '0 8px 32px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(147,51,234,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)')
                : (isDarkMode 
                  ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 6px 24px rgba(34,197,94,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)')
            }}
          >
            <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg">
              <Plus className="w-4 h-4 mr-2" />
              {t('banf.services.addService')}
            </div>
          </button>
        </div>
      </div>

      {/* Services List */}
      {(item.services || []).length === 0 ? (
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>{t('banf.services.empty')}</p>
          <p className="text-sm">{t('banf.services.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {(item.services || []).map((service, index) => (
            <ServiceItem
              key={service.id}
              service={service}
              index={index}
              isExpanded={expandedServices.has(service.id)}
              onToggleExpansion={toggleServiceExpansion}
              onRemove={removeService}
              onUpdate={updateService}
              units={units}
              themeColors={themeColors}
              kontierungstyp={item.kontierungstyp}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicesTab;